import {
  AlertTriangle,
  ArrowLeftRight,
  Ban,
  Bell,
  BookOpen,
  Globe,
  LayoutDashboard,
  Percent,
  Plug,
  ScrollText,
  Send,
  Server,
  Settings as SettingsIcon,
  Shield,
  ShieldCheck,
  Sliders,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import {type ComponentType, type ReactNode, lazy} from "react";

/* ------------------------------------------------------------------ */
/*  Type definitions                                                   */
/* ------------------------------------------------------------------ */

export interface NavEntry {
  /** Unique key used as route path (must start with /) */
  path: string;
  /** Label shown in the sidebar */
  label: string;
  /** Lucide icon element */
  icon: ReactNode;
  /** Lazy-loaded page component */
  component: ComponentType;
  /** Navigation section for sidebar grouping */
  section: NavSection;
  /** Hide from sidebar but keep as route (e.g. detail pages) */
  hidden?: boolean;
}

export type NavSection = "general" | "core" | "operations" | "onboarding" | "wallet" | "system";

export interface NavSectionConfig {
  key: NavSection;
  label: string;
}

/* ------------------------------------------------------------------ */
/*  Section definitions (in display order)                             */
/* ------------------------------------------------------------------ */

export const NAV_SECTIONS: NavSectionConfig[] = [
  {key: "general", label: "General"},
  {key: "core", label: "Core"},
  {key: "operations", label: "Operaciones"},
  {key: "onboarding", label: "Onboarding"},
  {key: "wallet", label: "Wallet"},
  {key: "system", label: "Sistema"},
];

/* ------------------------------------------------------------------ */
/*  Lazy page imports                                                  */
/* ------------------------------------------------------------------ */

const Home = lazy(() => import("@/pages/Dashboard/Home").then(m => ({default: m.Home})));
const UsersPage = lazy(() => import("@/pages/Users/Users").then(m => ({default: m.Users})));
const Countries = lazy(() => import("@/pages/Countries/Countries").then(m => ({default: m.Countries})));
const Transactions = lazy(() => import("@/pages/Transactions").then(m => ({default: m.Transactions})));
const Transfers = lazy(() => import("@/pages/Transfers/Transfers").then(m => ({default: m.Transfers})));
const Remittances = lazy(() => import("@/pages/Remittances/Remittances").then(m => ({default: m.Remittances})));
const KYC = lazy(() => import("@/pages/KYC/KYC").then(m => ({default: m.KYC})));
const Wallets = lazy(() => import("@/pages/Wallets/Wallets").then(m => ({default: m.Wallets})));
const ExchangeRates = lazy(() => import("@/pages/ExchangeRates/ExchangeRates").then(m => ({default: m.ExchangeRates})));
const Commissions = lazy(() => import("@/pages/Commissions/Commissions").then(m => ({default: m.Commissions})));
const ProvidersPage = lazy(() => import("@/pages/Providers/Providers").then(m => ({default: m.Providers})));
const ProviderMethodsPage = lazy(() =>
  import("@/pages/ProviderMethods/ProviderMethods").then(m => ({default: m.ProviderMethods}))
);
const FxRateConfigsPage = lazy(() =>
  import("@/pages/FxRateConfigs/FxRateConfigs").then(m => ({default: m.FxRateConfigs}))
);
const CommissionLedgerPage = lazy(() =>
  import("@/pages/CommissionLedger/CommissionLedger").then(m => ({default: m.CommissionLedger}))
);
const Settings = lazy(() => import("@/pages/Settings/Settings").then(m => ({default: m.Settings})));
const Levels = lazy(() => import("@/pages/Levels/Levels").then(m => ({default: m.Levels})));
const Blacklist = lazy(() => import("@/pages/Blacklist/Blacklist").then(m => ({default: m.Blacklist})));
const CompliancePage = lazy(() => import("@/pages/Compliance/Compliance").then(m => ({default: m.Compliance})));
const AuditLog = lazy(() => import("@/pages/AuditLog/AuditLog").then(m => ({default: m.AuditLog})));

// Push Notifications
const PushDashboard = lazy(() => import("@/pages/PushNotifications").then(m => ({default: m.PushDashboard})));
const CampaignHistory = lazy(() => import("@/pages/PushNotifications").then(m => ({default: m.CampaignHistory})));
const CampaignDetail = lazy(() => import("@/pages/PushNotifications").then(m => ({default: m.CampaignDetail})));
const CampaignCreator = lazy(() => import("@/pages/PushNotifications").then(m => ({default: m.CampaignCreator})));
const SendToUser = lazy(() => import("@/pages/PushNotifications").then(m => ({default: m.SendToUser})));

/* ------------------------------------------------------------------ */
/*  Icon size (shared constant)                                        */
/* ------------------------------------------------------------------ */

const I = 18;

/* ------------------------------------------------------------------ */
/*  Navigation registry — single source of truth                       */
/*                                                                     */
/*  To add a new page:                                                 */
/*    1. Create the page component in src/pages/                       */
/*    2. Add an entry here                                             */
/*    3. Done — routes & sidebar update automatically                  */
/* ------------------------------------------------------------------ */

export const NAV_REGISTRY: NavEntry[] = [
  // ── General ──────────────────────────────────────────────────
  {path: "/dashboard", label: "Dashboard", icon: <LayoutDashboard size={I} />, component: Home, section: "general"},

  // ── Core ─────────────────────────────────────────────────────
  {path: "/users", label: "Usuarios", icon: <Users size={I} />, component: UsersPage, section: "core"},
  {path: "/countries", label: "Países", icon: <Globe size={I} />, component: Countries, section: "core"},
  {path: "/levels", label: "Niveles", icon: <Shield size={I} />, component: Levels, section: "core"},
  {path: "/blacklist", label: "Blacklist", icon: <Ban size={I} />, component: Blacklist, section: "core"},

  // ── Operations ───────────────────────────────────────────────
  {
    path: "/transactions",
    label: "Transacciones",
    icon: <ArrowLeftRight size={I} />,
    component: Transactions,
    section: "operations",
  },
  {path: "/transfers", label: "Transferencias", icon: <Send size={I} />, component: Transfers, section: "operations"},
  {path: "/remittances", label: "Remesas", icon: <Send size={I} />, component: Remittances, section: "operations"},
  {
    path: "/push-notifications",
    label: "Push Notifications",
    icon: <Bell size={I} />,
    component: PushDashboard,
    section: "operations",
  },
  {
    path: "/push-notifications/campaigns",
    label: "Campañas",
    icon: <Bell size={I} />,
    component: CampaignHistory,
    section: "operations",
    hidden: true,
  },
  {
    path: "/push-notifications/campaigns/:id",
    label: "Detalle Campaña",
    icon: <Bell size={I} />,
    component: CampaignDetail,
    section: "operations",
    hidden: true,
  },
  {
    path: "/push-notifications/campaigns/new",
    label: "Crear Campaña",
    icon: <Bell size={I} />,
    component: CampaignCreator,
    section: "operations",
    hidden: true,
  },
  {
    path: "/push-notifications/send-to-user",
    label: "Enviar a Usuario",
    icon: <Bell size={I} />,
    component: SendToUser,
    section: "operations",
    hidden: true,
  },

  // ── Onboarding ───────────────────────────────────────────────
  {path: "/kyc", label: "KYC", icon: <ShieldCheck size={I} />, component: KYC, section: "onboarding"},

  // ── Wallet ───────────────────────────────────────────────────
  {path: "/wallets", label: "Wallets", icon: <Wallet size={I} />, component: Wallets, section: "wallet"},
  {
    path: "/exchange-rates",
    label: "Tasas de Cambio",
    icon: <TrendingUp size={I} />,
    component: ExchangeRates,
    section: "wallet",
  },
  {path: "/commissions", label: "Comisiones", icon: <Percent size={I} />, component: Commissions, section: "wallet"},
  {
    path: "/commission-ledger",
    label: "Commission Ledger",
    icon: <BookOpen size={I} />,
    component: CommissionLedgerPage,
    section: "wallet",
  },
  {path: "/providers", label: "Proveedores", icon: <Server size={I} />, component: ProvidersPage, section: "wallet"},
  {
    path: "/provider-methods",
    label: "Métodos por País",
    icon: <Plug size={I} />,
    component: ProviderMethodsPage,
    section: "wallet",
  },
  {
    path: "/fx-rate-configs",
    label: "FX Rate Configs",
    icon: <Sliders size={I} />,
    component: FxRateConfigsPage,
    section: "wallet",
  },

  // ── System ───────────────────────────────────────────────────
  {path: "/settings", label: "Configuración", icon: <SettingsIcon size={I} />, component: Settings, section: "system"},
  {
    path: "/compliance",
    label: "Compliance",
    icon: <AlertTriangle size={I} />,
    component: CompliancePage,
    section: "system",
  },
  {path: "/audit-log", label: "Audit Log", icon: <ScrollText size={I} />, component: AuditLog, section: "system"},
];

/* ------------------------------------------------------------------ */
/*  Derived helpers                                                    */
/* ------------------------------------------------------------------ */

/** Visible sidebar entries (excludes hidden) */
export const getVisibleEntries = () => NAV_REGISTRY.filter(e => !e.hidden);

/** Entries grouped by section in display order */
export const getGroupedEntries = () => {
  const visible = getVisibleEntries();
  return NAV_SECTIONS.map(section => ({
    ...section,
    items: visible.filter(e => e.section === section.key),
  })).filter(g => g.items.length > 0);
};

/** PATHS constant derived from registry (backward compatible) */
// Note: keep using @/constants/routes for PATHS imports.
// The registry drives routing and sidebar; PATHS is for programmatic use.
